import type { AxiosRequestConfig } from 'axios'
import { serviceGo } from '@/utils/request'

// 分书数据：最近 7/30 天 · 所有用户，每本书一行。与「分书新增数据」（只看新注册用户）是两套口径。
// 后端每天凌晨预计算（Go internal/api/book_data.go），页面只读最新一天，口径说明以那边文件头为准。
// 管理员 + 白名单账号可看；白名单由管理员在「用户权限」页维护。

export type BookDataPeriod = 7 | 30
export type BookDataSource = 'all' | 'ad' // all=全部用户 | ad=投放用户（有广告归因记录）

export interface BookDataFruit {
  total: number
  free: number
  paid: number // 免费/付费按「免费优先」回放果肉流水拆分
}

export interface BookDataRow {
  book_id: number
  book_title: string
  word_count: number     // 已发布章节的稿费结算字数
  ai_node_count: number  // 已发布章节里的 AI 剧情数
  chapter_count: number  // 已发布章节数
  impression_uv: number  // 曝光人数 = 列表曝光 ∪ 首启直连进入
  enter_uv: number       // 实际有对白的进入人数，也是收益表人均消耗分母
  direct_enter_uv?: number // 首启直入；旧快照缺字段，不能当作 0
  list_impression_uv?: number
  list_click_uv?: number
  click_enter_uv?: number
  fruit: BookDataFruit[] // 下标对应 meta.fruit_scopes：0 = 全书，5/10/20 = 前 N 章
  chapters: number[]     // 完成第 1..max_chapter 章的人数
  msg: number[]          // 下标对应 meta.msg_thresholds：发消息 ≥ 阈值的人数
  pay: number[]          // 下标对应 meta.pay_thresholds：在该书消耗果肉 ≥ 阈值的人数（阈值 0 = 消耗 > 0）
}

// 表头元信息随数据下发，前端不写死阈值
export interface BookDataMeta {
  entry_funnel_version?: number
  fruit_scopes: number[]
  max_chapter: number
  msg_thresholds: number[]
  pay_thresholds: number[]
}

export interface BookDataBoard {
  run_date: string     // 数据日（窗口截至这天）；空串 = 还没有任何预计算数据
  period_days: number
  source: BookDataSource
  window_start: string // 窗口首日（含）
  window_end: string   // 窗口末日（含）
  generated_at: string // 计算完成时间
  meta: BookDataMeta
  rows: BookDataRow[]  // 后端已按全书总消耗降序
}

export async function getBookData(period: BookDataPeriod, source: BookDataSource): Promise<BookDataBoard> {
  const res = await serviceGo.get('/api_v2/admin/book-data', { params: { period, source } })
  return res.data.data
}

// 当前账号能否看「分书数据」（管理员恒为 true），决定菜单显不显示。
// 静默：探测失败只当没权限，不弹错误框——每个后台账号打开页面都会调一次。
export async function getBookDataAccess(): Promise<boolean> {
  const config: AxiosRequestConfig & { _silentError?: boolean } = { _silentError: true }
  try {
    const res = await serviceGo.get('/api_v2/admin/book-data/access', config)
    return !!res.data?.data?.allowed
  } catch {
    return false
  }
}

// ===== 白名单（仅管理员）=====

export interface BookDataWhitelistUser {
  user_id: number
  username: string
  role: string
  missing: boolean // 账号已注销 / 不存在
}

// 增删接口都返回增删后的完整名单，直接替换列表即可
export async function getBookDataWhitelist(): Promise<BookDataWhitelistUser[]> {
  const res = await serviceGo.get('/api_v2/admin/book-data/whitelist')
  return res.data.data || []
}

export async function addBookDataWhitelist(userId: number): Promise<BookDataWhitelistUser[]> {
  const res = await serviceGo.post('/api_v2/admin/book-data/whitelist', { user_id: userId })
  return res.data.data || []
}

export async function removeBookDataWhitelist(userId: number): Promise<BookDataWhitelistUser[]> {
  const res = await serviceGo.delete('/api_v2/admin/book-data/whitelist', { params: { user_id: userId } })
  return res.data.data || []
}
